<script setup lang="ts">
const {
  filters,
  isFiltering,
  sortBy,
  searchTerm,
  appliedFiltersCount,
  applyFilters,
  clearFilters,
} = useFilters();
const { trackEvent } = useGAnalytics();
const isOpen = ref(false);

const handleApply = async () => {
  trackEvent("click_filters_apply");
  await applyFilters();
  isOpen.value = false;
};

const handleClear = async () => {
  trackEvent("click_filters_cancel");
  isFiltering.value = true;
  await delay(1);
  isOpen.value = false;
  isFiltering.value = false;
  clearFilters();
};
</script>

<template>
  <button
    class="ml-auto min-w-[195px] flex items-center justify-center w-full h-12 sm:justify-normal sm:w-auto gap-2 text-neutral-300 border border-secondary-200 rounded-sm p-2"
    @click="
      (isOpen = !isOpen), trackEvent(`click_filters_${String($route.name)}`)
    "
    data-test="button-toggle-filters-drawer">
    <NuxtIcon name="mdi:filter-variant" class="w-6 h-6" />
    <p>
      {{ $t("main.toolbar.showFilters") }}
      {{ appliedFiltersCount > 0 ? `(${appliedFiltersCount})` : "" }}
    </p>
  </button>
  <HeadlessTransitionRoot as="template" :show="isOpen">
    <HeadlessDialog as="div" class="relative z-50" @close="isOpen = false">
      <HeadlessTransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-neutral-800/85 transition-opacity" />
      </HeadlessTransitionChild>
      <div class="fixed inset-0 z-10">
        <div class="flex justify-end text-center items-center">
          <HeadlessTransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-x-full"
            enter-to="opacity-100 translate-x-0"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-x-0"
            leave-to="opacity-0 translate-x-full">
            <HeadlessDialogPanel
              class="w-full sm:w-96 max-h-dvh min-h-dvh transform overflow-hidden bg-neutral-800 text-left shadow-xl flex flex-col">
              <div
                v-if="isFiltering"
                class="absolute h-full w-full bg-neutral-800/70 z-10">
                <LoaderSpinner
                  size="2xl"
                  class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div
                class="flex items-center justify-between border-b border-secondary-500 w-full pl-8 pr-5 py-3 sm:py-8">
                <HeadlessDialogTitle
                  class="text-neutral-200 text-xl font-bold"
                  >{{ $t("main.toolbar.filter.filterBy") }}</HeadlessDialogTitle
                >
                <NuxtIcon
                  name="mdi:close"
                  class="w-9 h-9 ml-auto text-neutral-300 cursor-pointer"
                  @click="isOpen = false" />
              </div>
              <div class="overflow-y-scroll flex-1">
                <ToolbarFiltersSearch
                  v-model:searchTerm="searchTerm"
                  @do-search="applyFilters" />
                <ToolbarFiltersSort
                  v-model:sortBy="sortBy"
                  :sort-names="collectionSortNames" />
                <template
                  v-for="(filter, filterKey) in filters.options"
                  :key="filterKey">
                  <ToolbarFiltersOptions
                    :filter="filter"
                    :filter-key="(filterKey as string)" />
                </template>
                <template
                  v-for="(filter, filterKey) in filters.range"
                  :key="filterKey">
                  <ToolbarFiltersRange
                    :title="(filterKey as string)"
                    :min-price="filter.min"
                    :max-price="filter.max"
                    v-model:selected-min="filter.selectedMin"
                    v-model:selected-max="filter.selectedMax" />
                </template>
              </div>
              <div class="flex gap-4 justify-around mt-auto py-6 px-7">
                <BaseButtonSubmit
                  :text="$t('main.toolbar.filter.apply')"
                  class="flex-1"
                  :disabled="isFiltering"
                  @click="handleApply"
                  data-test="button-apply-filters"
                  :contextual-styles="true" />
                <BaseButtonCancel
                  :text="$t('main.toolbar.filter.clear')"
                  class="flex-1 bg-neutral-800"
                  :disabled="isFiltering"
                  @click="handleClear"
                  data-test="button-clear-filters"
                  :contextual-styles="true" />
              </div>
            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>
